import { getCurrency } from 'utils/utils';
import { PackageSearch } from '../interfaces/Package';
import { Select } from 'ts/interfaces/Generic';

export class PackageFilterModel {
  currencySign: string;
  name: {
    value: string;
    options: Select[];
  };
  fastOK: boolean;
  provider: {
    value: string;
    options: Select[];
  };
  stars: {
    count: number;
    checked: boolean;
    minPrice: number;
    disabled?: boolean;
  }[];
  nights: {
    count: number;
    checked: boolean;
    minPrice: number;
    packagesCount: number;
  }[];
  price: {
    min: number;
    max: number;
    value: [number, number];
  };
  destination: {
    name: string;
    price: number;
    checked: boolean;
  }[];
  destinationReturn: {
    name: string;
    price: number;
    checked: boolean;
  }[];
  features: {
    name: string;
    price: number;
    checked: boolean;
    displayName: string;
  }[];
  boards: {
    name: string;
    price: number;
    checked: boolean;
  }[];

  constructor(packages: PackageSearch[], checkedNight: number, isNetravel?: boolean) {
    if (!packages.length) {
      this.currencySign = '';
      this.name = { options: [], value: '' };
      this.fastOK = false;
      this.provider = { options: [], value: '' };
      this.stars = [];
      this.nights = [];
      this.price = { min: 0, max: 1, value: [0, 1] };
      this.destination = [];
      this.destinationReturn = [];
      this.features = [];
      this.boards = [];
      return;
    }

    this.currencySign = getCurrency(packages[0].currency);
    this.fastOK = false;

    const uniqueKeysMap = new Map<string, number>();
    const uniqueValuesMap = new Map<string, number>();
    const uniqueProvidersMap = new Map<string, number>();
    const uniqueDestinationsMap = new Map<string, number>();
    const uniqueBoardsMap = new Map<string, number>();
    const uniqueFeaturesMap = new Map<string, number>();
    const uniqueNightsMap = new Map<number, { minPrice: number; count: number }>();
    const uniqueStarsMap = new Map<number, number>();
    const uniqueDestinationReturnMap = new Map<string, number>();

    for (const pkg of packages) {
      uniqueValuesMap.set(
        pkg.rowName,
        Math.min(uniqueValuesMap.get(pkg.rowName) ?? Infinity, pkg.salePrice),
      );
      uniqueKeysMap.set(
        pkg.uniqueKey,
        Math.min(uniqueKeysMap.get(pkg.uniqueKey) ?? Infinity, pkg.salePrice),
      );
      uniqueProvidersMap.set(
        pkg.provider,
        Math.min(uniqueProvidersMap.get(pkg.provider) ?? Infinity, pkg.salePrice),
      );

      uniqueDestinationReturnMap.set(
        pkg.dealDestinationReturn,
        Math.min(
          uniqueDestinationReturnMap.get(pkg.dealDestinationReturn) ?? Infinity,
          pkg.salePrice,
        ),
      );

      const destination = pkg.cityFullDescription || pkg.dealDestination;
      uniqueDestinationsMap.set(
        destination,
        Math.min(uniqueDestinationsMap.get(destination) ?? Infinity, pkg.salePrice),
      );

      uniqueBoardsMap.set(
        pkg.boardingDescription,
        Math.min(uniqueBoardsMap.get(pkg.boardingDescription) ?? Infinity, pkg.salePrice),
      );

      const nights = parseInt(pkg.dealLength);
      const nightData = uniqueNightsMap.get(nights) || { minPrice: Infinity, count: 0 };
      uniqueNightsMap.set(nights, {
        minPrice: Math.min(nightData.minPrice, pkg.salePrice),
        count: nightData.count + 1,
      });

      const stars = parseInt(pkg.hotelRating);
      uniqueStarsMap.set(stars, Math.min(uniqueStarsMap.get(stars) ?? Infinity, pkg.salePrice));

      if (pkg.listOfDealFeature) {
        for (const feature of pkg.listOfDealFeature) {
          if (!feature.includes('fastOK')) {
            uniqueFeaturesMap.set(
              feature,
              Math.min(uniqueFeaturesMap.get(feature) ?? Infinity, pkg.salePrice),
            );
          }
        }
      }
    }

    this.name = {
      options: Array.from(uniqueValuesMap.keys())
        .map((val) => ({ value: val, label: val }))
        .sort((a, b) => a.label.localeCompare(b.label)),
      value: '',
    };

    this.provider = {
      options: Array.from(uniqueProvidersMap.keys())
        .map((val) => ({ value: val, label: val }))
        .sort((a, b) => a.label.localeCompare(b.label)),
      value: '',
    };

    this.stars = [5, 4, 3, 2, 1].map((val) => ({
      count: val,
      checked: false,
      minPrice: uniqueStarsMap.get(val) ?? 0,
      disabled: !uniqueStarsMap.has(val),
    }));

    this.nights = Array.from(uniqueNightsMap.entries())
      .sort(([a], [b]) => a - b)
      .map(([count, { minPrice, count: packagesCount }]) => ({
        count,
        checked: count === checkedNight,
        minPrice,
        packagesCount,
      }));

    if (isNetravel) {
      const hasMore3Stars = this.stars.some((star) => star.count > 3);
      if (hasMore3Stars) {
        this.stars = this.stars.map((star) => ({
          ...star,
          checked: star.count > 3,
        }));
      }

      const has3Nights = this.nights.some((night) => night.count === 3);
      const has4Nights = this.nights.some((night) => night.count === 4);

      if (has3Nights) {
        this.nights = this.nights.map((night) => ({
          ...night,
          checked: night.count === 3,
        }));
      } else if (has4Nights) {
        this.nights = this.nights.map((night) => ({
          ...night,
          checked: night.count === 4,
        }));
      } else if (this.nights.length > 0) {
        this.nights = this.nights.map((night, index) => ({
          ...night,
          checked: index === 0,
        }));
      }
    } else if (this.nights.length) {
      this.nights[0].checked = true;
    }

    const prices = Array.from(uniqueKeysMap.values());
    const minPrice = Math.floor(Math.min(...prices));
    const maxPrice = Math.ceil(Math.max(...prices));
    this.price = {
      min: minPrice,
      max: maxPrice,
      value: [minPrice, maxPrice],
    };

    this.destination = Array.from(uniqueDestinationsMap.entries()).map(([name, price]) => ({
      name,
      price,
      checked: false,
    }));
    this.destinationReturn = Array.from(uniqueDestinationReturnMap.entries()).map(
      ([name, price]) => ({
        name,
        price,
        checked: false,
      }),
    );

    this.features = Array.from(uniqueFeaturesMap.entries()).map(([name, price]) => ({
      name,
      price,
      displayName: name.match(/[a-zA-Z ]+/g)?.join('') || '',
      checked: false,
    }));

    this.boards = Array.from(uniqueBoardsMap.entries()).map(([name, price]) => ({
      name,
      price,
      checked: false,
    }));
  }
}
